import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';

/**
 * Watches every MatDialog.open() call and, once the dialog's view is
 * initialized, walks all `<mat-form-field>` elements inside it to
 * upgrade legacy patterns:
 *
 *   1. Empty `<mat-label></mat-label>` + `placeholder="Foo"` on the
 *      input  →  copy `Foo` into the label so the floating-label slot
 *      becomes active.
 *   2. Missing `<mat-label>` entirely + `placeholder="Foo"` on the
 *      input  →  inject a `<mat-label>` with that text.
 *
 * This lets us modernize ~35 legacy dialogs without editing each
 * template. New dialogs that already use `<mat-label>` correctly are
 * unaffected because the directive only mutates empty labels.
 *
 * Installed once in `AppModule` (see `app.module.ts`).
 */
@Injectable({providedIn: 'root'})
export class DialogLabelUpgraderService {
  private wired = false;

  constructor(private dialog: MatDialog) {}

  /** Call once at app bootstrap (e.g. from AppComponent ctor). */
  install(): void {
    if (this.wired) {
      return;
    }
    this.wired = true;
    this.dialog.afterOpened.subscribe(ref => {
      // Two ticks: one for Angular's view, one for Material's MDC
      // label slot setup.
      setTimeout(() => {
        const host = (ref as any)._containerInstance?._elementRef?.nativeElement as HTMLElement | undefined;
        if (host) {
          const mutated = this.upgrade(host);
          if (mutated) {
            // Material caches notched-outline widths during view init.
            // Dispatch a resize so it re-measures and the floating
            // label / outline notch fit the (now non-empty) label.
            window.dispatchEvent(new Event('resize'));
          }
        }
      }, 0);
    });
  }

  private upgrade(root: HTMLElement): boolean {
    let mutated = false;
    const fields = root.querySelectorAll('mat-form-field');
    fields.forEach(f => {
      if (this.upgradeOne(f as HTMLElement)) {
        mutated = true;
      }
    });
    return mutated;
  }

  private upgradeOne(field: HTMLElement): boolean {
    const label = field.querySelector(':scope > mat-label') as HTMLElement | null;
    const input =
      (field.querySelector('input[placeholder], textarea[placeholder]') as HTMLElement | null) ||
      (field.querySelector('mat-select[placeholder]') as HTMLElement | null);

    const placeholderText = input?.getAttribute('placeholder')?.trim() || '';
    if (!placeholderText) {
      return false;
    }

    // Material's MDC outline renders the floating label from the
    // projected <mat-label> element's text content. If a template
    // ships an empty <mat-label></mat-label> we copy the placeholder
    // text into it AND also into Material's pre-rendered floating
    // label span so the visual updates without a full re-init.
    if (label && !label.textContent?.trim()) {
      label.textContent = placeholderText;
      const floating = field.querySelector('.mdc-floating-label, .mat-mdc-floating-label') as HTMLElement | null;
      if (floating && !floating.textContent?.trim()) {
        floating.textContent = placeholderText;
      }
      return true;
    }

    // No <mat-label> at all → with outline appearance, Material
    // already shows the placeholder inside the input, so there is
    // nothing further to do.
    return false;
  }
}

